import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Users, BookOpen, UserRoundCheck, GraduationCap, Presentation, Award, AlertTriangle } from "lucide-react";

const Stat = ({ icon: Icon, label, value, to, tone = "text-[#1C352D]", testId }) => (
  <Link to={to} className="block" data-testid={testId}>
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors duration-150 h-full">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={12}/> {label}</div>
      <div className={`text-3xl font-semibold mt-2 ${tone}`}>{value ?? "—"}</div>
    </Card>
  </Link>
);

export default function ModularDashboard() {
  const { data: d } = useQuery({ queryKey: ["modular-dashboard"], queryFn: async () => (await api.get("/modular/dashboard")).data });
  const years = Object.entries(d?.by_year || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="modular-dashboard">
      <PageHeader title="Scuola Modulare" subtitle="Percorso modulare: studio online, weekend intensivi in presenza e dirette Zoom."/>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Studenti attivi" value={d?.students_active} to="/modulare/studenti" testId="mod-stat-students"/>
        <Stat icon={BookOpen} label="Corsi" value={d?.courses} to="/modulare/corsi" testId="mod-stat-courses"/>
        <Stat icon={UserRoundCheck} label="Tutor attivi" value={d?.tutors} to="/modulare/tutor" testId="mod-stat-tutors"/>
        <Stat icon={GraduationCap} label="Docenti" value={d?.teachers} to="/modulare/docenti" testId="mod-stat-teachers"/>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none lg:col-span-2" data-testid="mod-by-year">
          <div className="text-sm font-medium mb-3">Studenti attivi per anno</div>
          {years.length === 0 ? <div className="text-sm text-stone-400">Nessuno studente modulare attivo.</div> : (
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {years.map(([y, n]) => (
                <div key={y} className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">{y === "—" ? "n.d." : `${y}° anno`}</div>
                  <div className="text-xl font-semibold text-[#1C352D]">{n}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <div className="space-y-4">
          {d?.without_tutor > 0 && (
            <Link to="/modulare/tutor" data-testid="mod-without-tutor">
              <Card className="p-4 border border-amber-200 bg-amber-50 rounded-xl shadow-none flex items-center gap-3">
                <AlertTriangle size={18} className="text-amber-700"/>
                <div className="text-sm text-amber-900"><b>{d.without_tutor}</b> studenti senza tutor assegnato</div>
              </Card>
            </Link>
          )}
          <Stat icon={Presentation} label="Seminari e workshop" value={d?.seminars} to="/modulare/seminari" testId="mod-stat-seminars"/>
          <Stat icon={Award} label="Attestati" value="Genera" to="/modulare/attestati" tone="text-[#E2725B] text-xl" testId="mod-stat-certs"/>
        </div>
      </div>
    </div>
  );
}
